import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import type { Entitlements, QuotaStatus, WorkspaceAllowance } from '@29100/shared';
import { api } from '../lib/api';
import type { WorkspaceKind } from '../lib/navigation';
import { useSession } from '../lib/session';
import type { WorkspaceSummary } from '../lib/types';
import { useQuery } from './useApi';

const CLAVE = '29100.workspace';

/**
 * El espacio elegido, uno solo para toda la aplicación.
 *
 * Estuvo en un `useState` dentro del hook, y eso significaba una copia por
 * componente: el selector cambiaba la suya y nadie más se enteraba, así que
 * pasar de empresa a personal dejaba el menú y el dashboard mostrando
 * «Equipo», «Informes» y las carpetas compartidas de la organización que ya no
 * estaba abierta. Sólo se arreglaba recargando la página, que es cuando todas
 * las copias volvían a leer `localStorage`.
 *
 * Con un valor compartido y `useSyncExternalStore`, cambiar de espacio
 * repinta a la vez todo lo que depende de él.
 */
const seleccion = {
  actual: leerGuardado(),
  oyentes: new Set<() => void>(),
};

function leerGuardado(): string | null {
  // Un navegador con el almacenamiento bloqueado no debe tirar la aplicación:
  // sin recuerdo se entra al primer espacio, que es un buen valor por defecto.
  try {
    return localStorage.getItem(CLAVE);
  } catch {
    return null;
  }
}

function recordar(workspaceId: string): void {
  try {
    localStorage.setItem(CLAVE, workspaceId);
  } catch {
    /* Recordarlo es una comodidad, no un requisito. */
  }
}

function suscribir(oyente: () => void): () => void {
  seleccion.oyentes.add(oyente);
  return () => {
    seleccion.oyentes.delete(oyente);
  };
}

function elegido(): string | null {
  return seleccion.actual;
}

function elegir(workspaceId: string): void {
  if (seleccion.actual === workspaceId) return;
  seleccion.actual = workspaceId;
  recordar(workspaceId);
  for (const oyente of [...seleccion.oyentes]) oyente();
}

/**
 * Workspace activo.
 *
 * Es el eje de todo el producto: personal, familiar y de empresa son el mismo
 * motor con distinto workspace, así que el selector de contexto no cambia de
 * aplicación, cambia este identificador. Se recuerda en `localStorage` para
 * que recargar no devuelva al usuario a otro espacio, y se valida contra la
 * sesión —si ya no tiene acceso, se cae al primero suyo en lugar de pedir
 * datos de un espacio ajeno.
 */
export function useWorkspace() {
  const { session } = useSession();
  const workspaces = useMemo<WorkspaceSummary[]>(() => session?.workspaces ?? [], [session]);

  const actual = useSyncExternalStore(suscribir, elegido, elegido);

  const activo = useMemo(() => {
    const encontrado = workspaces.find((w) => w.workspace.id === actual);
    return encontrado ?? workspaces[0] ?? null;
  }, [workspaces, actual]);

  // Cuando se cae al primero —sesión recién cargada, o acceso retirado al que
  // estaba guardado— el recuerdo se pone al día para que el resto de la
  // aplicación vea exactamente el mismo espacio que esta pantalla.
  useEffect(() => {
    if (activo) elegir(activo.workspace.id);
  }, [activo]);

  const cambiar = useCallback((workspaceId: string) => elegir(workspaceId), []);

  const esEmpresa = activo?.workspace.type === 'organization';
  // Tipo del espacio activo, que es lo que decide qué secciones se le ofrecen.
  const tipo: WorkspaceKind = esEmpresa ? 'organization' : 'personal';

  return {
    workspaces,
    active: activo,
    workspaceId: activo?.workspace.id ?? null,
    isOrganization: esEmpresa,
    kind: tipo,
    switchTo: cambiar,
  };
}

export interface WorkspaceDetail {
  workspace: WorkspaceSummary['workspace'];
  role: string;
  entitlements: Entitlements;
  quota: QuotaStatus;
}

/** Detalle del workspace: plan, funciones y cuota, siempre desde la API. */
export function useWorkspaceDetail(workspaceId: string | null) {
  return useQuery<WorkspaceDetail>(workspaceId ? `/api/workspaces/${workspaceId}` : null);
}

export function useStorage(workspaceId: string | null) {
  const query = useQuery<{ quota: QuotaStatus }>(
    workspaceId ? `/api/workspaces/${workspaceId}/quota` : null,
  );

  return { ...query, quota: query.data?.quota ?? null };
}

/**
 * Cupo de espacios de empresa del titular.
 *
 * Se pregunta a la API: el número sale del plan contratado y de lo que ya
 * tiene abierto, y ninguna de las dos cosas se puede deducir en el navegador.
 */
export function useWorkspaceAllowance() {
  const query = useQuery<{ allowance: WorkspaceAllowance }>('/api/workspaces/allowance');
  return { ...query, allowance: query.data?.allowance ?? null };
}

export async function createWorkspace(input: {
  type: 'personal' | 'organization';
  name: string;
  profileCode?: string | null;
  profileConfig?: Record<string, unknown>;
  organization?: { name: string };
}) {
  return api<{ workspace: WorkspaceSummary['workspace'] }>('/api/workspaces', {
    method: 'POST',
    body: input,
  });
}
